import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCallbackEnabled, useRunCallback } from "../../state/callbacks/callbacks";
import { useIsValidForm } from "../../state/InternalContext";
import { isClient } from "../../globals";
import { findFirstComponentOfType, TextExtractor } from "../internal/TextExtractor";

interface CallbackButtonProps {
  callback: string,
  requireValidForm: boolean,
  label?: string,
  children: React.ReactNode,
}

/***
 * A button that calls a callback function.
 * Callback function must be added to the globalCallbacks object or the sharedState.callbacks object.
 * The button label can either be supplied in the label property or as content of the CallbackButton
 * element.
 */
export function CallbackButton({ callback, requireValidForm, label, children }: CallbackButtonProps): React.ReactElement {
  const runCallback = useRunCallback();
  const callbackEnabled = useCallbackEnabled();
  const isValidForm = useIsValidForm();
  const useNavigateOnClient = isClient ? useNavigate : () => () => { };
  const navigate = useNavigateOnClient();

  // Get the label from the label child
  const labelChild = useMemo(() => findFirstComponentOfType(children, "arg-label"), [children]);
  const [labelValue, setLabelValue] = useState("");

  // Derive enabled/disabled from callback and validity signals.
  // Compute on every render (not `useMemo`) because `callbackEnabled()` can depend on values
  // that change without changing its function identity.
  const callbackIsEnabled = callbackEnabled(callback);
  const formIsValidFromCallback = callbackEnabled("__is-valid-form");
  const formIsValid = !requireValidForm || formIsValidFromCallback || !!isValidForm;
  const enabled = callbackIsEnabled && formIsValid;
  const disabled = !enabled;

  const clickHandler: React.MouseEventHandler<HTMLButtonElement> = useCallback((_event: React.MouseEvent<HTMLButtonElement>) => {
    runCallback(callback);
    if (isClient) {
      const navigateTo = window?.location?.pathname + window?.location?.hash + window?.location?.search || "";
      navigate(navigateTo, { replace: true }); // Force update
    }
  }, [callback, navigate, runCallback]);

  const button = useMemo(() => {
    const resolvedLabel = label !== undefined ? label : labelValue;
    if (disabled) {
      // Use condition because React SSR messes up the attribute
      return <button disabled={disabled}>{resolvedLabel}</button>
    }
    else {
      return <button onClick={clickHandler}>{resolvedLabel}</button>
    }
  }, [disabled, label, labelValue, clickHandler]);

  return <>
    {label === undefined && labelChild && (
      <TextExtractor onExtractedText={setLabelValue}>
        {labelChild?.props.children}
      </TextExtractor>
    )}
    {button}
  </>;
}